/**
 * GSTR-1A on the Returns screen (FR-E13, B-07; V-10).
 *
 * On a FILED GSTR-1: what has changed since it was filed, and the one control — *Open a
 * GSTR-1A for this month* — offered only where a change exists and no 1A is open yet; once
 * one is, a link to it. On a GSTR-1A: what the correction carries (amended, added, and the
 * filed documents the books no longer have — listed, never removed), where the filed side
 * came from, and the GSTR-1 it corrects.
 *
 * Read-only apart from the one button: every count and every row came from the service's
 * diff; nothing here sums or decides (ADR-0014).
 */
import { Alert, Button, Card } from '@fiveox/ui'

import { type Gstr1aBlock, type PeriodGates } from '../lib/api'
import { changeWords, filedSourceWords, gstr1aOffer } from '../lib/gstr1a'

const HEADS = ['taxable', 'igst', 'cgst', 'sgst', 'cess'] as const

/** Indian grouping, and a sign on a difference — as AmendmentsCard shows it. */
function rupees(paise: number | undefined | null, signed = false): string {
  if (paise === null || paise === undefined) return '—'
  const value = paise / 100
  const text = Math.abs(value).toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })
  if (!signed) return value < 0 ? `(${text})` : text
  if (value === 0) return '0.00'
  return value > 0 ? `+${text}` : `−${text}`
}

type Doc = Record<string, unknown>

function text(doc: Doc, key: string): string {
  const value = doc[key]
  return value === null || value === undefined ? '—' : String(value)
}

function heads(doc: Doc, key?: string): Record<string, number | undefined> {
  const source = (key ? (doc[key] as Doc | undefined) : doc) ?? {}
  return Object.fromEntries(HEADS.map((h) => [h, source[h] as number | undefined]))
}

function AmendedRows({ rows }: { rows: Doc[] }) {
  return (
    <div className="gst-table-wrap mb-4">
      <table className="gst-table">
        <thead>
          <tr>
            <th className="is-text">Section</th>
            <th className="is-text">Counterparty GSTIN</th>
            <th className="is-text">Document</th>
            <th>Filed taxable</th>
            <th>Revised taxable</th>
            {HEADS.map((h) => (
              <th key={h}>Δ {h}</th>
            ))}
            <th className="is-text">Fields</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => {
            const fields = (r.fields as Record<string, { filed: string; revised: string }> | undefined) ?? {}
            return (
              <tr key={`${text(r, 'section')}|${text(r, 'ctin')}|${text(r, 'doc_no')}`} className={i % 2 ? 'is-a' : undefined}>
                <td className="is-text">{text(r, 'section').toUpperCase()}</td>
                <td className="is-text">
                  <span className="gst-mono">{text(r, 'ctin')}</span>
                </td>
                <td className="is-text">{text(r, 'doc_no')}</td>
                <td>{rupees(heads(r, 'filed').taxable)}</td>
                <td>{rupees(heads(r, 'revised').taxable)}</td>
                {HEADS.map((h) => (
                  <td key={h}>{rupees(heads(r, 'diff')[h], true)}</td>
                ))}
                <td className="is-text">
                  {Object.entries(fields)
                    .map(([name, change]) => `${name}: ${change.filed} → ${change.revised}`)
                    .join('; ') || '—'}
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

function DocList({ rows }: { rows: Doc[] }) {
  return (
    <ul className="gst-meta mb-3 list-disc pl-5">
      {rows.map((d) => (
        <li key={`${text(d, 'section')}|${text(d, 'ctin')}|${text(d, 'doc_no')}`}>
          {text(d, 'section').toUpperCase()} · {text(d, 'doc_no')}
          {d.ctin ? ` · ${String(d.ctin)}` : ''} · taxable {rupees(heads(d).taxable)}
        </li>
      ))}
    </ul>
  )
}

function Contents({ block }: { block: Gstr1aBlock }) {
  const uncompared = Object.entries(block.uncompared ?? {})
  return (
    <>
      {uncompared.length > 0 && (
        <Alert tone="warning" className="mt-3">
          The 1A payload will hold until these sections can be compared:{' '}
          {uncompared.map(([name, why]) => `${name.toUpperCase()} — ${why}`).join('; ')}.
        </Alert>
      )}
      {(block.amended?.length ?? 0) > 0 && (
        <div className="mt-4">
          <p className="gst-sub mb-2">Amended — written in the amendment sections with the original named</p>
          <AmendedRows rows={block.amended as Doc[]} />
        </div>
      )}
      {(block.added?.length ?? 0) > 0 && (
        <>
          <p className="gst-sub mb-2">Added — not in the filed return, written in the ordinary sections</p>
          <DocList rows={block.added as Doc[]} />
        </>
      )}
      {(block.deleted?.length ?? 0) > 0 && (
        <>
          <p className="gst-sub mb-2">Filed, and no longer in the books — listed, never removed</p>
          <DocList rows={block.deleted as Doc[]} />
          <p className="gst-meta mb-3">
            Removing a filed invoice is a decision, not a correction the payload makes on its own.
          </p>
        </>
      )}
    </>
  )
}

export function Gstr1aCard({
  gates,
  canCreate,
  busy,
  onOpen,
  onSelect,
}: {
  gates: PeriodGates
  canCreate: boolean
  busy: boolean
  /** Open the 1A for this filed GSTR-1's month. */
  onOpen: () => void
  /** Select another period in the rail (the 1A, or the GSTR-1 it corrects). */
  onSelect: (periodId: string) => void
}) {
  const block = gates.gstr1a
  if (!block) return null

  if (gates.period.return_type === 'GSTR1A') {
    return (
      <Card className="mt-6 p-4">
        <p className="gst-panel-h mb-1">What this GSTR-1A corrects</p>
        <p className="gst-meta mb-1">
          {changeWords(block)}
          {block.filed_source ? ` Compared ${filedSourceWords(block.filed_source)}.` : ''}
          {block.unchanged ? ` ${block.unchanged.toLocaleString()} unchanged.` : ''}
        </p>
        {block.gstr1_period_id && (
          <p className="gst-meta">
            <button type="button" className="gst-link" onClick={() => onSelect(block.gstr1_period_id!)}>
              Open the filed GSTR-1 for {gates.period.period}
            </button>
          </p>
        )}
        <p className="gst-meta mt-2">
          GSTN&rsquo;s offline-utility template has no GSTR-1A sheet, so this correction has a client
          workbook and a JSON and no utility workbook.
        </p>
        <Contents block={block} />
      </Card>
    )
  }

  const offer = gstr1aOffer(gates.period, block)
  if (offer.kind === 'none') return null
  return (
    <Card className="mt-6 p-4">
      <p className="gst-panel-h mb-1">A correction to this month</p>
      <p className="gst-meta mb-3">
        A change after this month&rsquo;s GSTR-1 was filed is a GSTR-1A of this month — its own
        draft of the changed rows, its own approval and filing — never an amendment in the next
        month. {offer.words}
      </p>
      <div className="flex flex-wrap items-center gap-2">
        {offer.kind === 'open' && canCreate && (
          <Button type="button" size="sm" disabled={busy} onClick={onOpen}>
            Open a GSTR-1A for this month
          </Button>
        )}
        {offer.kind === 'exists' && (
          <Button type="button" size="sm" variant="outline" onClick={() => onSelect(offer.gstr1aPeriodId)}>
            Open the GSTR-1A
          </Button>
        )}
      </div>
      {block.available && (offer.kind === 'open' || offer.kind === 'exists') && (
        <Contents block={block} />
      )}
    </Card>
  )
}
